import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Spin,
  Typography,
  message,
} from 'antd'
import axios from 'axios'
import { AppCollAccesses, AccessCollaborator } from './AppCollAccesses'

interface SelectOption {
  value: string
  label: string
}

/** Ant Design Select keys/selection by `value`; keep sector name while making each option unique. */
const CONQUEST_OPTION_SEP = '\u0001'

function toConquestOptionValue(sectorName: string, index: number): string {
  return `${sectorName}${CONQUEST_OPTION_SEP}${index}`
}

function fromConquestOptionValue(optionValue: string): string {
  return optionValue.split(CONQUEST_OPTION_SEP)[0] ?? optionValue
}

interface CollaboratorFormValues {
  prename: string
  lastname: string
  email: string
  phone_number: string
  rank?: number
  admin_access: string
  password: string
  mobile_required: string
  matricule?: string | null
  conquest_sector_name?: string | null
  datajson?: string | null
  adhocaccess?: boolean | null
  cn_softaccess_access?: boolean | null
}

interface CnSoftAccessInfo {
  softaccess_id: string
  ms_pro: boolean
}

interface CollaboratorFormData {
  orga_id: string
  orga_name: string
  coll_id?: string | null
  suggestedPassword: string
  hierarchies: SelectOption[]
  conquestSectors: SelectOption[]
  adminAccessOptions: SelectOption[]
  mobileRequiredOptions: SelectOption[]
  trueFalseOptions: SelectOption[]
  accessCollaborators: AccessCollaborator[]
  cnSoftAccess?: CnSoftAccessInfo | null
  values: CollaboratorFormValues
}

interface CollaboratorFormProps {
  orgaId: string
  collId?: string
}

interface SaveResponse {
  ok: boolean
  redirect?: string
  errors?: Map<string, unknown>
}

function flattenPlayErrors(errors: SaveResponse['errors']): Map<string, string> {
  if (!errors) {
    return new Map<string, string>()
  }
  const result: Map<string, string> = new Map<string, string>()
  errors.forEach((value, key) => {
    if (typeof value === 'string') {
      result.set(key, value)
      return
    }
    if (Array.isArray(value)) {
      result.set(key, value
        .map(item => {
          if (typeof item === 'string') {
            return item
          } else if (item && typeof item === 'object' && 'msg' in item) {
            const msg = (item as { msg: string | string[] }).msg
            return Array.isArray(msg) ? msg.join(', ') : String(msg)
          } else {
            return String(item)
          }
        })
        .join(', '))
    }
  })
  return result
}

export function CollaboratorForm({ orgaId, collId }: CollaboratorFormProps) {
  const [form] = Form.useForm<CollaboratorFormValues>()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<CollaboratorFormData | null>(null)
  const isEdit = Boolean(collId)
  const cnSoftAccessAccess = Form.useWatch('cn_softaccess_access', form)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const { data } = await axios.get<CollaboratorFormData>(
          `/organisations/${orgaId}/collaborators/form`,
          { params: collId ? { coll_id: collId } : {} },
        )
        setFormData(data)
        const conquestOptions = data.conquestSectors.filter(o => o.value)
        const storedConquest = data.values.conquest_sector_name
        const conquestMatchIndex =
          storedConquest == null || storedConquest === ''
            ? -1
            : conquestOptions.findIndex(o => o.value === storedConquest)
        form.setFieldsValue({
          ...data.values,
          conquest_sector_name:
            conquestMatchIndex >= 0
              ? toConquestOptionValue(storedConquest!, conquestMatchIndex)
              : storedConquest,
          adhocaccess: data.values.adhocaccess ?? false,
          datajson: data.values.datajson ?? '',
          cn_softaccess_access: data.values.cn_softaccess_access ?? false,
        })
      } catch (error) {
        console.error(error)
        message.error('Impossible de charger le formulaire collaborateur')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [orgaId, collId, form])

  const onFinish = async (values: CollaboratorFormValues) => {
    setSaving(true)
    try {
      const body = new URLSearchParams()
      body.set('prename', values.prename || '')
      body.set('lastname', values.lastname || '')
      body.set('email', values.email || '')
      body.set('phone_number', values.phone_number || '')
      body.set('rank', String(values.rank ?? ''))
      body.set('admin_access', values.admin_access || '')
      body.set('password', values.password || '')
      body.set('mobile_required', values.mobile_required || '')
      if (values.matricule) {
        body.set('matricule', values.matricule)
      }
      if (values.conquest_sector_name) {
        body.set(
          'conquest_sector_name',
          fromConquestOptionValue(values.conquest_sector_name),
        )
      }
      body.set('datajson', values.datajson || '')
      body.set('adhocaccess', String(values.adhocaccess ?? false))
      body.set('cn_softaccess_access', String(values.cn_softaccess_access ?? false))
      body.set('facturation.facturable', 'true')
      body.set('facturation.super_admin', 'false')

      const url = isEdit
        ? `/organisations/${orgaId}/collaborators/${collId}`
        : `/organisations/${orgaId}/collaborators`

      const { data } = await axios.post<SaveResponse>(url, body, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        validateStatus: status => status < 500,
      })

      if (data.ok && data.redirect) {
        message.success('Collaborateur enregistré')
        window.location.href = data.redirect
        return
      }

      const fieldErrors = flattenPlayErrors(data.errors)
      if (Object.keys(fieldErrors).length > 0) {
        form.setFields(
          Array.from(fieldErrors.entries()).map(([name, error]) => ({
            name: name as keyof CollaboratorFormValues,
            errors: [error],
          })),
        )
        message.error('Veuillez corriger les erreurs du formulaire')
      } else {
        message.error("Erreur lors de l'enregistrement")
      }
    } catch (error) {
      console.error(error)
      message.error("Erreur lors de l'enregistrement")
    } finally {
      setSaving(false)
    }
  }

  if (loading || !formData) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="collaborator-form">
      <div style={{ marginBottom: 8 }}>
        <Typography.Title level={4}>
          {isEdit ? 'Modification collaborateur' : 'Nouveau collaborateur'}
        </Typography.Title>
      </div>

      <Alert
        message="Les utilisateurs dont l'adresse email se termine par @admin.fr n'apparaîtront pas dans la liste des collaborateurs dans 'Gestion' ou dans les options pour les affectations."
        type="info"
        showIcon
        style={{ marginBottom: 8 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        requiredMark
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} lg={12}>
            <Card style={{ marginBottom: 16 }} title="Informations générales" size="small">
              <Form.Item
                label="Prénom"
                name="prename"
                rules={[{ required: true, message: 'Prénom requis' }]}
                className="no-box-shadow"
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Nom"
                name="lastname"
                rules={[{ required: true, message: 'Nom requis' }]}
                className="no-box-shadow"
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Email requis' },
                  { type: 'email', message: 'Email invalide' },
                ]}
                className="no-box-shadow"
              >
                <Input />
              </Form.Item>

              <Form.Item label="Mot de passe" className="no-box-shadow">
                <Form.Item name="password" noStyle>
                  <Input.Password autoComplete="new-password" style={{ width: '100%' }} />
                </Form.Item>
                <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Typography.Text>
                    Suggestion : {formData.suggestedPassword}
                  </Typography.Text>
                  <Button
                    type="link"
                    onClick={() => {
                      form.setFieldValue('password', formData.suggestedPassword)
                    }}
                  >
                    Appliquer la suggestion
                  </Button>
                </div>
              </Form.Item>

              <Form.Item
                label="Numéro de téléphone mobile"
                name="phone_number"
                rules={[
                  { required: true, message: 'Téléphone requis' },
                  { len: 10, message: 'Le numéro doit contenir 10 chiffres (ex: 06xxxxxxxx)' },
                ]}
                extra="+33 — saisir le numéro au format 0XXXXXXXXX"
                className="no-box-shadow"
              >
                <Input maxLength={10} />
              </Form.Item>

              <Form.Item label="Matricule" name="matricule" className="no-box-shadow">
                <Input />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card style={{ marginBottom: 16 }} title="Organisation" size="small">
              <Form.Item
                label="Rang hiérarchique"
                name="rank"
                rules={[{ required: true, message: 'Rang requis' }]}
                className="no-box-shadow"
              >
                <Select
                  options={formData.hierarchies.map(o => ({
                    value: Number(o.value),
                    label: o.label,
                  }))}
                  allowClear
                />
              </Form.Item>

              <Form.Item label="Secteur conquest" name="conquest_sector_name" className="no-box-shadow">
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={formData.conquestSectors
                    .filter(o => o.value)
                    .map((o, index) => ({
                      value: toConquestOptionValue(o.value, index),
                      label: o.label,
                    }))}
                  allowClear
                />
              </Form.Item>
            </Card>

            <Card title="Accès & droits" size="small">
              {formData.cnSoftAccess && (
                <>
                  <Form.Item
                    label={formData.cnSoftAccess.ms_pro ? 'Accès à MsPro' : 'Accès à S&C'}
                    name="cn_softaccess_access"
                    className="no-box-shadow"
                  >
                    <Radio.Group
                      options={formData.trueFalseOptions.map(o => ({
                        value: o.value === 'true',
                        label: o.label,
                      }))}
                    />
                  </Form.Item>
                  {cnSoftAccessAccess === false && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message={
                        formData.cnSoftAccess.ms_pro
                          ? "Cette organisation dispose d'un accès MsPro. Ce collaborateur n'y a pas accès. Activez l'option ci-dessus pour lui donner l'accès."
                          : "Cette organisation dispose d'un accès S&C. Ce collaborateur n'y a pas accès. Activez l'option ci-dessus pour lui donner l'accès."
                      }
                    />
                  )}
                </>
              )}

              <Form.Item
                label="Administration des secteurs et administrateurs"
                name="admin_access"
                className="no-box-shadow"
              >
                <Select options={formData.adminAccessOptions} allowClear />
              </Form.Item>

              <Form.Item label="Authentification mobile" name="mobile_required" className="no-box-shadow">
                <Select options={formData.mobileRequiredOptions} allowClear />
              </Form.Item>

              <Form.Item label="Accès adhoc" name="adhocaccess" className="no-box-shadow">
                <Radio.Group
                  options={formData.trueFalseOptions.map(o => ({
                    value: o.value === 'true',
                    label: o.label,
                  }))}
                />
              </Form.Item>

              <Form.Item
                label={'Accès "en tant que"'}
                name="datajson"
                trigger="onChange"
                valuePropName="value"
                className="no-box-shadow"
              >
                <AppCollAccesses colls={formData.accessCollaborators} />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <div style={{ position: 'sticky', bottom: 0, zIndex: 10, display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8, padding: 14, background: 'rgba(255, 255, 255, 0.92)' }}>
          <Button href={`/organisations/${orgaId}/collaborators`}>Annuler</Button>
          <Button type="primary" htmlType="submit" loading={saving}>
            Enregistrer
          </Button>
        </div>
      </Form>
      <style>{`
        .ant-form-item.no-box-shadow {
          box-shadow: none !important;
          margin: 5px !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  )
}
